import { dbService } from '../firebaseInstance';

/**
 * 제목 자동 생성 (첫번째 문장에서)
 */
const getAutoTitle = (threads) => {
  const mainThread = threads.find(t => t.isMain);
  if (!mainThread || !mainThread.text.trim()) {
    return '제목 없음';
  }
  
  const text = mainThread.text.trim();
  // 첫 50자까지만 사용
  if (text.length <= 50) {
    return text;
  }
  return text.substring(0, 47) + '...';
};

/**
 * 1. 초안 저장 (CREATE/UPDATE)
 * @param {string} userID - 사용자 ID
 * @param {Object} draftData - 초안 데이터 {threads, mediaFiles, collectionConfig}
 * @param {string|null} draftId - 기존 초안 ID (없으면 새로 생성)
 * @returns {Promise<Object>} 결과 객체
 */
export const saveDraft = async (userID, draftData, draftId = null) => {
  try {
    console.log('[saveDraft] Start:', { userID, draftId, hasThreads: !!draftData.threads });
    
    if (!userID) {
      throw new Error('사용자 ID가 필요합니다.');
    }

    if (!draftData.threads || !Array.isArray(draftData.threads)) {
      throw new Error('스레드 데이터가 필요합니다.');
    }

    const draftsRef = dbService.collection('user').doc(userID).collection('drafts');
    
    // mediaFiles를 그대로 저장 (이제 File 객체는 포함되지 않음)
    const sanitizedMediaFiles = draftData.mediaFiles || [];
    const sanitizedReplyMedia = draftData.replyMediaFiles || {};
  
  const dataToSave = {
    threads: draftData.threads,
    mediaFiles: sanitizedMediaFiles,
    replyMediaFiles: sanitizedReplyMedia,
      collectionConfig: draftData.collectionConfig || { mode: "none", autoAddToCollection: false },
      scheduleDateTime: draftData.scheduleDateTime ? draftData.scheduleDateTime.toISOString() : null, // ISO 형식으로 저장
      updatedAt: Date.now()
    };
    
    let result;
    
    if (draftId) {
      // 기존 초안이 존재하는지 먼저 확인
      const draftDoc = await draftsRef.doc(draftId).get();
      
      if (draftDoc.exists) {
        // 문서가 존재하면 업데이트
        await draftsRef.doc(draftId).update(dataToSave);
        console.log('[saveDraft] ✅ Draft updated:', draftId);
        result = { success: true, draftId: draftId, action: 'updated' };
      } else {
        // 문서가 존재하지 않으면 새로 생성
        console.log('[saveDraft] ⚠️ Draft not found, creating new one instead of updating:', draftId);
        dataToSave.createdAt = Date.now();
        const docRef = await draftsRef.add(dataToSave);
        console.log('[saveDraft] ✅ Draft created (fallback):', docRef.id);
        result = { success: true, draftId: docRef.id, action: 'created' };
      }
    } else {
      // 새 초안 생성
      dataToSave.createdAt = Date.now();
      const docRef = await draftsRef.add(dataToSave);
      console.log('[saveDraft] ✅ Draft created:', docRef.id);
      result = { success: true, draftId: docRef.id, action: 'created' };
    }
    
    return result;
  } catch (error) {
    console.error('[saveDraft] ❌ Save draft failed:', error);
    throw new Error(`초안 저장 실패: ${error.message}`);
  }
};

/**
 * 2. 초안 목록 조회 (READ)
 * @param {string} userID - 사용자 ID
 * @returns {Promise<Array>} 초안 목록 배열
 */
export const getDrafts = async (userID) => {
  try {
    console.log('[getDrafts] Start:', { userID });
    
    if (!userID) {
      throw new Error('사용자 ID가 필요합니다.');
    }

    // orderBy 없이 먼저 시도 (updatedAt 필드가 없을 수 있음)
    const draftsSnapshot = await dbService
      .collection('user')
      .doc(userID)
      .collection('drafts')
      .get();
    
    console.log('[getDrafts] Raw snapshot:', {
      empty: draftsSnapshot.empty,
      size: draftsSnapshot.size,
      docs: draftsSnapshot.docs.length
    });
    
    const drafts = draftsSnapshot.docs.map(doc => {
      const data = doc.data();
      console.log('[getDrafts] Processing draft doc:', doc.id, data);
      
      // updatedAt이 숫자인지 확인하고 Date로 변환
      const updatedAt = data.updatedAt ? 
        (typeof data.updatedAt === 'number' ? new Date(data.updatedAt) : data.updatedAt.toDate()) 
        : new Date();
      const createdAt = data.createdAt ? 
        (typeof data.createdAt === 'number' ? new Date(data.createdAt) : data.createdAt.toDate()) 
        : new Date();
      
      return {
        id: doc.id,
        ...data,
        title: getAutoTitle(data.threads), // 자동 제목 생성
        createdAt: createdAt,
        updatedAt: updatedAt
      };
    });
    
    // updatedAt으로 정렬
    drafts.sort((a, b) => b.updatedAt - a.updatedAt);
    
    console.log('[getDrafts] ✅ Drafts loaded:', drafts.length, 'items', drafts);
    return drafts;
  } catch (error) {
    console.error('[getDrafts] ❌ Get drafts failed:', error);
    throw new Error(`초안 목록 조회 실패: ${error.message}`);
  }
};

/**
 * 3. 초안 삭제 (DELETE)
 * @param {string} userID - 사용자 ID
 * @param {string} draftId - 삭제할 초안 ID
 * @returns {Promise<Object>} 결과 객체
 */
export const deleteDraft = async (userID, draftId) => {
  try {
    console.log('[deleteDraft] Start:', { userID, draftId });
    
    if (!userID) {
      throw new Error('사용자 ID가 필요합니다.');
    }

    if (!draftId) {
      throw new Error('초안 ID가 필요합니다.');
    }

    await dbService
      .collection('user')
      .doc(userID)
      .collection('drafts')
      .doc(draftId)
      .delete();
    
    console.log('[deleteDraft] ✅ Draft deleted:', draftId);
    return { success: true, draftId: draftId };
  } catch (error) {
    console.error('[deleteDraft] ❌ Delete draft failed:', error);
    throw new Error(`초안 삭제 실패: ${error.message}`);
  }
};

/**
 * 4. 초안을 PostThreads 에디터로 로드
 * @param {string} userID - 사용자 ID
 * @param {string} draftId - 로드할 초안 ID
 * @returns {Promise<Object>} 초안 데이터
 */
export const loadDraftToEditor = async (userID, draftId) => {
  try {
    console.log('[loadDraftToEditor] Start:', { userID, draftId });
    
    if (!userID) {
      throw new Error('사용자 ID가 필요합니다.');
    }

    if (!draftId) {
      throw new Error('초안 ID가 필요합니다.');
    }

    const draftDoc = await dbService
      .collection('user')
      .doc(userID)
      .collection('drafts')
      .doc(draftId)
      .get();
    
    if (!draftDoc.exists) {
      throw new Error('초안을 찾을 수 없습니다.');
    }
    
    const draftData = draftDoc.data();
    
    // 미디어 파일 복원 (이제 모두 업로드된 파일들)
    const restoredMediaFiles = draftData.mediaFiles || [];
    
    const result = {
      id: draftId,
      threads: draftData.threads || [{ id: 1, text: "", isMain: true }],
      mediaFiles: restoredMediaFiles,
      replyMediaFiles: draftData.replyMediaFiles || {},
      collectionConfig: draftData.collectionConfig || { mode: "none", autoAddToCollection: false },
      scheduleDateTime: draftData.scheduleDateTime || null
    };
    
    console.log('[loadDraftToEditor] ✅ Draft loaded:', draftId);
    return result;
  } catch (error) {
    console.error('[loadDraftToEditor] ❌ Load draft failed:', error);
    throw new Error(`초안 로드 실패: ${error.message}`);
  }
};
